import { useCallback, useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/Common";
import { Markdown } from "@/components/chat/Markdown";
import { api, downloadCvDocx } from "@/lib/api";
import { exportToPDF } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Mail, Gauge, Compass, Download, Trash2, FolderOpen, Eye } from "lucide-react";

const META = {
  cv: { icon: FileText, label: "CV", color: "text-blue-600 bg-blue-50" },
  cover_letter: { icon: Mail, label: "Cover Letter", color: "text-purple-600 bg-purple-50" },
  predictor: { icon: Gauge, label: "Prediction", color: "text-orange-dark bg-orange/10" },
  orientation: { icon: Compass, label: "Orientation", color: "text-green-600 bg-green-50" },
  orientation_report: { icon: Compass, label: "Orientation Report", color: "text-green-600 bg-green-50" },
};


function renderContent(doc) {
  if (doc.type === "predictor") {
    try {
      const r = JSON.parse(doc.content);
      return `**Chance: ${r.probability}% — ${r.verdict}**\n\n${r.summary}\n\n**Strengths**\n${(r.strengths || []).map((s) => `- ${s}`).join("\n")}\n\n**To improve**\n${(r.improvements || []).map((s) => `- ${s}`).join("\n")}\n\n**Tips**\n${(r.tips || []).map((s) => `- ${s}`).join("\n")}`;
    } catch { return doc.content; }
  }
  if (doc.type === "orientation") {
    try { const r = JSON.parse(doc.content); return `**Profile: ${r.profile_title}**\n\nFields: ${r.fields.join(", ")}\n\n${r.report}`; }
    catch { return doc.content; }
  }
  return doc.content;
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(() => api.get("/tools/documents").then((r) => setDocs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/tools/documents/${id}`);
    toast.success("Deleted");
    setActive(null);
    load();
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-5 py-8">
        <PageHeader title="My Documents" subtitle="All your generated CVs, letters, predictions and orientation results — saved and ready to download." />
        {docs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-border">
            <FolderOpen className="h-14 w-14 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No documents yet. Use the tools to generate your first one.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((d) => {
              const m = META[d.type] || META.cv;
              const Icon = m.icon;
              return (
                <div key={d.id} className="bg-white rounded-2xl border border-border shadow-card p-5 flex flex-col" data-testid={`doc-${d.id}`}>
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center mb-3 ${m.color}`}><Icon className="h-5 w-5" /></div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-orange">{m.label}</span>
                  <h3 className="font-display font-extrabold italic text-navy leading-tight mt-1 flex-1">{d.title}</h3>
                  <p className="text-xs text-muted-foreground mt-2">{new Date(d.created_at).toLocaleDateString()}</p>
                  <div className="flex gap-2 mt-4">
                    <Button data-testid={`doc-view-${d.id}`} size="sm" variant="outline" onClick={() => setActive(d)} className="rounded-full flex-1"><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                    {d.format ? (
                      <Button size="sm" data-testid={`doc-${d.format === "pdf" ? "pdf" : "word"}-${d.id}`} title={d.format === "pdf" ? "Download PDF" : "Download Word"} onClick={async () => { try { await downloadCvDocx(d.id, d.title, d.format); } catch { toast.error("Download failed"); } }} className="rounded-full bg-navy hover:bg-navy-deep text-white"><Download className="h-3.5 w-3.5" /></Button>
                    ) : (
                      <Button size="sm" onClick={() => exportToPDF(d.title, renderContent(d), d.title.toLowerCase().replace(/\s+/g, "-"))} className="rounded-full bg-navy hover:bg-navy-deep text-white"><Download className="h-3.5 w-3.5" /></Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`document-delete-${d.id}`} onClick={() => del(d.id)} className="rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-black italic uppercase text-navy">{active?.title}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Document preview</DialogDescription>
          {active && <Markdown>{renderContent(active)}</Markdown>}
          {active && (
            <div className="flex gap-2 pt-4 border-t border-border">
              {active.format ? (
                <Button data-testid={`doc-dialog-${active.format === "pdf" ? "pdf" : "word"}`} onClick={async () => { try { await downloadCvDocx(active.id, active.title, active.format); } catch { toast.error("Download failed"); } }} className="rounded-full bg-navy hover:bg-navy-deep text-white"><Download className="h-4 w-4 mr-2" /> Download {active.format === "pdf" ? "PDF" : "Word"}</Button>
              ) : (
                <Button onClick={() => exportToPDF(active.title, renderContent(active), active.title.toLowerCase().replace(/\s+/g, "-"))} className="rounded-full bg-navy hover:bg-navy-deep text-white"><Download className="h-4 w-4 mr-2" /> Download PDF</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
